import { watchSeat, watchedSeats, unwatchSeat } from "../lib/inbox";
import { Link } from "react-router-dom";
import { type SeatCandidate } from "@anjabom/core";
import { useEffect, useState } from "react";
import { TipComposer } from "./TipComposer";
import { contributionOrQueue, seatKey } from "../lib/contribution-client";
import { isAppsInTossChannel } from "../lib/channel";
export function SeatContributions({
	candidate,
	onPublished,
}: {
	candidate: SeatCandidate;
	onPublished: () => void;
}) {
	const [tipChanged, setTipChanged] = useState(false);
	const [compose, setCompose] = useState(false),
		[message, setMessage] = useState(""),
		[show, setShow] = useState(candidate.instanceLabel ?? ""),
		[config, setConfig] = useState("proscenium"),
		[reports, setReports] = useState<
			Array<{ id: string; stage_config: string }>
		>([]),
		[error, setError] = useState("");
	const key = seatKey(candidate);
	const [watching, setWatching] = useState(() => watchedSeats().includes(key));
	useEffect(() => {
		if (!show.trim()) {
			setReports([]);
			return;
		}
		const controller = new AbortController();
		fetch(
			`/api/stage-reports?${new URLSearchParams({ venue: candidate.venueId, space: candidate.spaceId, show })}`,
			{ signal: controller.signal },
		)
			.then(async (r) => {
				if (!r.ok) throw new Error();
				setReports(
					(
						(await r.json()) as {
							reports: Array<{ id: string; stage_config: string }>;
						}
					).reports,
				);
				setError("");
			})
			.catch(() => {
				if (!controller.signal.aborted)
					setError("무대 구성 제보를 불러오지 못했어요.");
			});
		return () => controller.abort();
	}, [candidate.venueId, candidate.spaceId, show]);
	if (isAppsInTossChannel()) return null;
	if (candidate.layoutVersionId.endsWith("-unconfirmed")) return <section className="stack"><p>직접 입력한 좌표는 아직 공식 배치와 연결하지 않았어요. 공개 기록·관심 자리·궁금해요는 좌석도에서 자리를 확인한 뒤 남겨 주세요.</p><Link className="button" to={`/start?venue=${candidate.venueId}`}>좌석도에서 이 자리 확인하기</Link></section>;
	async function request() {
		try {
			const previous = JSON.parse(
				localStorage.getItem("anjabom.requested-seats.v1") ?? "[]",
			) as string[];
			if (previous.includes(key)) {
				setMessage("이 기기에서 이미 궁금해요를 남겼어요.");
				return;
			}
			const result = await contributionOrQueue<{ count: number }>(
				"/api/seat-requests",
				{ seatKey: key },
			);
			localStorage.setItem(
				"anjabom.requested-seats.v1",
				JSON.stringify([...previous, key]),
			);
			const watchResult = watchSeat(key);
			setWatching(watchResult === "added");
			setMessage(
				result.queued
					? "궁금해요는 기기에 보관했어요. 연결되면 전송해요. 아직 공개 요청 수에는 더하지 않았어요."
					: `궁금해요 요청 ${result.data.count}건을 모았어요.${watchResult === "limit" ? " 무료 관심 자리는 다섯 개까지예요." : ""}`,
			);
		} catch (e) {
			setMessage(e instanceof Error ? e.message : "연결을 확인해 주세요.");
		}
	}
	async function stage() {
		try {
			const result = await contributionOrQueue<{ id: string }>(
				"/api/stage-reports",
				{
					venueId: candidate.venueId,
					spaceId: candidate.spaceId,
					showTitle: show,
					stageConfig: config,
					note: "",
					observedMonth: new Date().toISOString().slice(0, 7),
				},
			);
			if (result.queued) {
				setMessage(
					"무대 구성 제보를 기기에 보관했어요. 연결되면 전송해요. 아직 공개되지 않았어요.",
				);
				return;
			}
			setReports([...reports, { id: result.data.id, stage_config: config }]);
			setMessage(
				"무대 구성 제보를 남겼어요. 공식 확인 전이며 배치를 자동으로 바꾸지 않아요.",
			);
		} catch (e) {
			setMessage(e instanceof Error ? e.message : "제보하지 못했어요.");
		}
	}
	const labels: Record<string, string> = {
		proscenium: "일반 무대",
		thrust: "돌출무대",
		center: "센터 무대",
		standing: "스탠딩 포함",
	};
	return (
		<section className="stack">
			<button
				className="button"
				aria-pressed={watching}
				onClick={() => {
					if (watching) {
						unwatchSeat(key);
						setWatching(false);
					} else {
						const result = watchSeat(key);
						if (result === "added") setWatching(true);
						else
							setMessage(
								result === "limit"
									? "무료 관심 자리는 다섯 개까지예요. 시즌 패스는 아직 결제를 열지 않았어요."
									: "이 기기에 저장하지 못했어요.",
							);
					}
				}}
			>
				{watching ? "★ 소식 받는 자리" : "☆ 이 자리 소식 받기"}
			</button>
			{message.includes("다섯 개") ? (
				<Link to="/pass?trigger=watch_limit">시즌 패스 안내 보기</Link>
			) : null}
			<div className="inline">
				<button
					className="button button-primary"
					type="button"
					onClick={() => setCompose(true)}
				>
					이 자리 한 줄 남기기
				</button>
				<button className="button" type="button" onClick={() => void request()}>
					이 자리 궁금해요
				</button>
			</div>
			<p role="status">{message}</p>
			<details className="stack">
				<summary>이 공연 무대 구성 알려 주기</summary>
				<label className="field">
					공연 이름
					<input
						className="input"
						value={show}
						onChange={(e) => setShow(e.target.value)}
						maxLength={100}
					/>
				</label>
				{error ? (
					<p role="alert">{error}</p>
				) : !show ? (
					<p>공연 이름을 넣으면 같은 공연 제보를 볼 수 있어요.</p>
				) : reports.length ? (
					<p>
						무대 구성 제보 {reports.length}건:{" "}
						{[...new Set(reports.map((r) => labels[r.stage_config]))].join(
							" · ",
						)}{" "}
						·{" "}
						{new Set(reports.map((r) => r.stage_config)).size > 1
							? "제보가 엇갈려요"
							: "공식 확인 전"}
					</p>
				) : (
					<p>아직 제보가 없어요.</p>
				)}
				<label className="field">
					무대 구성
					<select
						className="input"
						value={config}
						onChange={(e) => setConfig(e.target.value)}
					>
						{Object.entries(labels).map(([value, label]) => (
							<option key={value} value={value}>
								{label}
							</option>
						))}
					</select>
				</label>
				<button
					className="button"
					type="button"
					disabled={!show.trim()}
					onClick={() => void stage()}
				>
					무대 구성 제보하기
				</button>
			</details>
			{compose ? (
				<TipComposer
					seatKey={key}
					onClose={() => { setCompose(false); if (tipChanged) { setTipChanged(false); onPublished(); } }}
					onPublished={() => setTipChanged(true)}
				/>
			) : null}
		</section>
	);
}
